/**
 * Aura Viva — shared modal + toast helper.
 *
 * Used by:
 *  - assets/av-cart-tier-guard.js  (removal-block warning, single OK button)
 *  - assets/av-cart-link.js        (link / unlink confirmation, two buttons)
 *
 * Global API:
 *
 *   AVModal.show({
 *     title: 'Title',
 *     body: 'Body text. May include \n for line breaks.',
 *     bodyHtml: '<p>or raw HTML</p>',     // alternative to `body`
 *     confirmText: 'Confirm',              // required for two-button mode
 *     cancelText: 'Cancel',                // omit → single-OK mode
 *     okText: 'Ok',                        // for single-OK mode
 *     onConfirm: () => { ... },            // called when user clicks confirm
 *     onCancel: () => { ... },             // optional
 *     variant: 'primary' | 'danger',       // visual emphasis on confirm
 *   });
 *
 *   AVModal.toast({
 *     message: 'Linked.',
 *     variant: 'success' | 'info' | 'warning',
 *     durationMs: 4000,
 *   });
 *
 * Brand palette: cream #EFE0C8, dark green #2C332F.
 * Closes on overlay click, ESC, or any button. Focus returns sensibly.
 */
(function () {
  "use strict";

  if (window.__AV_MODAL_LOADED__) return;
  window.__AV_MODAL_LOADED__ = true;

  /* ─── CSS (injected once) ────────────────────────────────────────── */

  function injectStyles() {
    if (document.getElementById("av-modal-styles")) return;
    var s = document.createElement("style");
    s.id = "av-modal-styles";
    s.textContent = [
      "@import url('https://fonts.googleapis.com/css2?family=Quattrocento+Sans:wght@400;700&family=EB+Garamond:wght@400;600&display=swap');",

      ".av-modal[hidden]{display:none!important;}",
      ".av-modal{position:fixed;inset:0;z-index:1000000;font-family:'Quattrocento Sans',sans-serif;}",
      ".av-modal__overlay{position:absolute;inset:0;background:rgba(44,51,47,.55);}",
      ".av-modal__dialog{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:calc(100% - 32px);max-width:440px;background:#EFE0C8;border-radius:16px;padding:26px 26px 22px;box-shadow:0 24px 60px rgba(44,51,47,.35);box-sizing:border-box;color:#2C332F;}",
      ".av-modal__title{font-family:'EB Garamond',serif;font-size:20px;font-weight:600;line-height:1.25;margin:0 0 12px;color:#1D1D1F;}",
      ".av-modal__body{font-size:14px;line-height:1.5;color:#2C332F;opacity:.9;margin-bottom:20px;white-space:pre-line;}",
      ".av-modal__body p{margin:0 0 10px;} .av-modal__body p:last-child{margin-bottom:0;}",
      ".av-modal__actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;}",
      ".av-modal__btn{border:none;border-radius:9px;padding:11px 22px;font:inherit;font-size:14px;font-weight:700;letter-spacing:.04em;cursor:pointer;transition:filter .15s,transform .05s;}",
      ".av-modal__btn:active{transform:translateY(1px);}",
      ".av-modal__btn--primary{background:#2C332F;color:#EFE0C8;}",
      ".av-modal__btn--primary:hover{filter:brightness(1.15);}",
      ".av-modal__btn--danger{background:#8B2A1E;color:#EFE0C8;}",
      ".av-modal__btn--danger:hover{filter:brightness(1.15);}",
      ".av-modal__btn--ghost{background:transparent;color:#2C332F;text-decoration:underline;text-underline-offset:3px;padding:11px 14px;}",
      ".av-modal__btn--ghost:hover{filter:none;opacity:.7;}",

      ".av-toast{position:fixed;left:50%;transform:translateX(-50%) translateY(20px);bottom:24px;z-index:999999;background:#2C332F;color:#EFE0C8;padding:13px 20px;border-radius:9px;font-family:'Quattrocento Sans',sans-serif;font-size:13.5px;font-weight:600;letter-spacing:.02em;max-width:calc(100% - 32px);box-shadow:0 12px 32px rgba(44,51,47,.32);opacity:0;transition:opacity .25s,transform .25s;pointer-events:none;}",
      ".av-toast.av-is-visible{opacity:1;transform:translateX(-50%) translateY(0);}",
      ".av-toast--success{background:#2C332F;}",
      ".av-toast--warning{background:#8B6B2A;}",
      ".av-toast--info{background:#3F5950;}",
    ].join("");
    document.head.appendChild(s);
  }

  /* ─── helpers ────────────────────────────────────────────────────── */

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /* ─── modal ──────────────────────────────────────────────────────── */

  var lastFocus = null;

  function close(rootEl) {
    if (!rootEl || !rootEl.parentNode) return;
    rootEl.parentNode.removeChild(rootEl);
    if (lastFocus && typeof lastFocus.focus === "function") {
      try { lastFocus.focus(); } catch (e) {}
      lastFocus = null;
    }
    document.removeEventListener("keydown", escHandler, true);
  }

  function escHandler(e) {
    if (e.key === "Escape") {
      var existing = document.querySelector(".av-modal");
      if (existing) {
        var cancelBtn = existing.querySelector("[data-av-modal-cancel],[data-av-modal-ok]");
        if (cancelBtn) cancelBtn.click();
        else close(existing);
      }
    }
  }

  function show(opts) {
    injectStyles();
    opts = opts || {};

    // remove any existing modal
    var existing = document.querySelector(".av-modal");
    if (existing) close(existing);

    lastFocus = document.activeElement;

    var root = document.createElement("div");
    root.className = "av-modal";
    root.setAttribute("role", "presentation");

    var bodyMarkup = opts.bodyHtml
      ? String(opts.bodyHtml)
      : esc(opts.body || "");

    var twoButton = !!opts.cancelText && !!opts.confirmText;
    var variant = opts.variant === "danger" ? "danger" : "primary";

    var buttonsHtml = twoButton
      ? '<button type="button" class="av-modal__btn av-modal__btn--ghost" data-av-modal-cancel>' + esc(opts.cancelText) + "</button>"
        + '<button type="button" class="av-modal__btn av-modal__btn--' + variant + '" data-av-modal-confirm>' + esc(opts.confirmText) + "</button>"
      : '<button type="button" class="av-modal__btn av-modal__btn--primary" data-av-modal-ok>' + esc(opts.okText || "Ok") + "</button>";

    root.innerHTML =
      '<div class="av-modal__overlay" data-av-modal-cancel></div>'
      + '<div class="av-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="av-modal-title">'
        + '<h2 id="av-modal-title" class="av-modal__title">' + esc(opts.title || "") + "</h2>"
        + '<div class="av-modal__body">' + bodyMarkup + "</div>"
        + '<div class="av-modal__actions">' + buttonsHtml + "</div>"
      + "</div>";

    document.body.appendChild(root);
    document.addEventListener("keydown", escHandler, true);

    var primaryBtn = root.querySelector("[data-av-modal-confirm],[data-av-modal-ok]");
    if (primaryBtn) primaryBtn.focus();

    root.addEventListener("click", function (e) {
      var t = e.target;
      if (!t) return;
      if (t.closest("[data-av-modal-confirm]")) {
        close(root);
        if (typeof opts.onConfirm === "function") {
          try { opts.onConfirm(); } catch (err) { console.error("[AVModal] onConfirm threw:", err); }
        }
      } else if (t.closest("[data-av-modal-cancel]")) {
        close(root);
        if (typeof opts.onCancel === "function") {
          try { opts.onCancel(); } catch (err) { console.error("[AVModal] onCancel threw:", err); }
        }
      } else if (t.closest("[data-av-modal-ok]")) {
        close(root);
        // single-OK is treated as a confirm
        if (typeof opts.onConfirm === "function") {
          try { opts.onConfirm(); } catch (err) { console.error("[AVModal] onConfirm threw:", err); }
        }
      }
    });
  }

  /* ─── toast ──────────────────────────────────────────────────────── */

  var toastTimers = [];

  function toast(opts) {
    injectStyles();
    opts = opts || {};

    var el = document.createElement("div");
    el.className = "av-toast av-toast--" + (opts.variant === "warning" ? "warning"
                                          : opts.variant === "info"    ? "info"
                                          : "success");
    el.setAttribute("role", "status");
    el.textContent = String(opts.message || "");

    document.body.appendChild(el);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add("av-is-visible"); });
    });

    var duration = Number.isFinite(opts.durationMs) ? opts.durationMs : 4000;
    var timer = setTimeout(function () {
      el.classList.remove("av-is-visible");
      setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 260);
    }, duration);
    toastTimers.push(timer);
  }

  window.AVModal = { show: show, toast: toast };
})();
